/**
 * Os menus do botão direito do Desk Cloud (docs/spec/desk-cloud.md, "Os menus do botão direito"), em
 * funções puras: cada uma recebe o alvo, o que a pessoa pode fazer e as ações, e devolve os itens do
 * `ContextMenu`. A tela só escolhe qual menu abrir; o teste confere os itens sem tela.
 */
import type { MenuEntry } from '../ui/ContextMenu.js';
import { FILE_KINDS, type DeskFile, type DeskFolder, type DeskRoom, type FileKind, type FileSort } from '../shared.js';
import { actionsFor, type UseAction } from './cloudModel.js';

type T = (key: string, params?: Record<string, string | number>) => string;

/** O que os menus fazem; quem abre o menu liga cada uma à tela. */
export type CloudActions = {
  open: (file: DeskFile) => void;
  use: (file: DeskFile, action: UseAction, roomId: string) => void;
  download: (files: DeskFile[]) => void;
  copyLink: (file: DeskFile) => void;
  rename: (file: DeskFile) => void;
  moveTo: (files: DeskFile[], folderId: string | null) => void;
  pickFolder: (files: DeskFile[]) => void;
  setKind: (files: DeskFile[], kind: FileKind) => void;
  favorite: (files: DeskFile[], on: boolean) => void;
  share: (files: DeskFile[]) => void;
  trash: (files: DeskFile[]) => void;
  restore: (files: DeskFile[]) => void;
  purge: (files: DeskFile[]) => void;
};

export type FolderActions = {
  openFolder: (folder: DeskFolder) => void;
  newFolder: (parentId: string | null) => void;
  uploadToFolder: (folder: DeskFolder) => void;
  renameFolder: (folder: DeskFolder) => void;
  moveFolder: (folder: DeskFolder) => void;
  zipFolder: (folder: DeskFolder) => void;
  deleteFolder: (folder: DeskFolder) => void;
};

/** O que o menu de um arquivo precisa saber: as mesas da pessoa e as pastas usadas por último. */
export type MenuContext = { t: T; rooms: readonly DeskRoom[]; recentFolders: readonly DeskFolder[] };

/** Quantas mesas o "Usar na mesa" lista (as outras, pela página de detalhes). */
const USE_ROOMS = 6;

const actionLabel = (t: T, action: UseAction) => t(`cloud.use.${action}`);

/**
 * O submenu "Usar na mesa": as ações do tipo, em cada mesa em que valem (as de mestre, só nas mesas em
 * que a pessoa mestra; mandar no chat, em qualquer mesa dela). As mesas ligadas ao arquivo vêm antes.
 */
export function roomUseEntries(t: T, file: DeskFile, rooms: readonly DeskRoom[], actions: Pick<CloudActions, 'use'>): MenuEntry[] {
  const ordered = [...rooms].sort((a, b) => Number(file.rooms.includes(b.id)) - Number(file.rooms.includes(a.id)));
  const out: MenuEntry[] = [];
  let listed = 0;
  for (const room of ordered) {
    const list = actionsFor(file, room.gm);
    if (!list.length) continue;
    if (listed++ >= USE_ROOMS) break;
    if (rooms.length > 1) out.push({ kind: 'title', label: room.name });
    for (const action of list) out.push({ label: rooms.length > 1 ? actionLabel(t, action) : `${actionLabel(t, action)} · ${room.name}`, onSelect: () => actions.use(file, action, room.id) });
  }
  return out;
}

/** "Mover para ▸": as pastas usadas por último, "Escolher pasta…" e "Tirar da pasta". */
function moveEntries(t: T, files: DeskFile[], recent: readonly DeskFolder[], actions: CloudActions): MenuEntry[] {
  const here = files.length === 1 ? files[0]!.folderId : null;
  const out: MenuEntry[] = recent.filter((folder) => folder.id !== here).slice(0, 5).map((folder) => ({ label: folder.name, onSelect: () => actions.moveTo(files, folder.id) }));
  if (out.length) out.push({ kind: 'separator' });
  out.push({ label: t('cloud.menu.pickFolder'), onSelect: () => actions.pickFolder(files) });
  if (files.some((file) => file.folderId !== null)) out.push({ label: t('cloud.menu.toRoot'), onSelect: () => actions.moveTo(files, null) });
  return out;
}

/** "Tipo ▸": os cinco tipos, com a marca no atual (quando todos são do mesmo tipo). */
function kindEntries(t: T, files: DeskFile[], actions: CloudActions): MenuEntry[] {
  const same = files.every((file) => file.kind === files[0]?.kind) ? files[0]?.kind : undefined;
  return FILE_KINDS.map((kind) => ({ label: t(`cloud.kind.${kind}`), checked: kind === same, onSelect: () => actions.setKind(files, kind) }));
}

/** O menu de um arquivo (fora da lixeira). */
export function fileMenu(ctx: MenuContext, file: DeskFile, actions: CloudActions): MenuEntry[] {
  const { t } = ctx;
  const use = roomUseEntries(t, file, ctx.rooms, actions);
  return [
    { kind: 'title', label: file.name },
    { label: t('cloud.menu.open'), shortcut: 'Enter', onSelect: () => actions.open(file) },
    ...(use.length ? [{ kind: 'submenu', label: t('cloud.menu.use'), entries: use } as const] : []),
    { label: t('cloud.menu.download'), onSelect: () => actions.download([file]) },
    { label: t('cloud.menu.copyLink'), onSelect: () => actions.copyLink(file) },
    { kind: 'separator' },
    { label: t('cloud.menu.rename'), shortcut: 'F2', onSelect: () => actions.rename(file) },
    { kind: 'submenu', label: t('cloud.menu.moveTo'), entries: moveEntries(t, [file], ctx.recentFolders, actions) },
    { kind: 'submenu', label: t('cloud.menu.kind'), entries: kindEntries(t, [file], actions) },
    file.favorite ? { label: t('cloud.menu.unfavorite'), onSelect: () => actions.favorite([file], false) } : { label: t('cloud.menu.favorite'), onSelect: () => actions.favorite([file], true) },
    { label: t('cloud.menu.share'), onSelect: () => actions.share([file]) },
    { kind: 'separator' },
    { label: t('cloud.menu.trash'), shortcut: 'Del', danger: true, onSelect: () => actions.trash([file]) },
  ];
}

/** O menu de vários selecionados (fora da lixeira). */
export function selectionMenu(ctx: MenuContext, files: DeskFile[], actions: CloudActions): MenuEntry[] {
  const { t } = ctx;
  const allFavorite = files.every((file) => file.favorite);
  return [
    { kind: 'title', label: t('cloud.selected', { n: files.length }) },
    { label: t('cloud.menu.zip'), onSelect: () => actions.download(files) },
    { label: t('cloud.menu.share'), onSelect: () => actions.share(files) },
    { kind: 'submenu', label: t('cloud.menu.moveTo'), entries: moveEntries(t, files, ctx.recentFolders, actions) },
    { kind: 'submenu', label: t('cloud.menu.kind'), entries: kindEntries(t, files, actions) },
    allFavorite ? { label: t('cloud.menu.unfavorite'), onSelect: () => actions.favorite(files, false) } : { label: t('cloud.menu.favorite'), onSelect: () => actions.favorite(files, true) },
    { kind: 'separator' },
    { label: t('cloud.menu.trash'), shortcut: 'Del', danger: true, onSelect: () => actions.trash(files) },
  ];
}

/** O menu do que está na lixeira (um ou vários). */
export function trashMenu(ctx: Pick<MenuContext, 't'>, files: DeskFile[], actions: Pick<CloudActions, 'restore' | 'purge'>): MenuEntry[] {
  const { t } = ctx;
  return [
    { kind: 'title', label: files.length === 1 ? files[0]!.name : t('cloud.selected', { n: files.length }) },
    { label: t('cloud.menu.restore'), onSelect: () => actions.restore(files) },
    { label: t('cloud.menu.purge'), danger: true, onSelect: () => actions.purge(files) },
  ];
}

/** O menu de uma pasta (na árvore ou no bloco). */
export function folderMenu(ctx: Pick<MenuContext, 't'>, folder: DeskFolder, actions: FolderActions): MenuEntry[] {
  const { t } = ctx;
  return [
    { kind: 'title', label: folder.name },
    { label: t('cloud.menu.open'), onSelect: () => actions.openFolder(folder) },
    { label: t('cloud.menu.newFolderHere'), onSelect: () => actions.newFolder(folder.id) },
    { label: t('cloud.menu.uploadHere'), onSelect: () => actions.uploadToFolder(folder) },
    { kind: 'separator' },
    { label: t('cloud.menu.rename'), onSelect: () => actions.renameFolder(folder) },
    { label: t('cloud.menu.moveFolder'), onSelect: () => actions.moveFolder(folder) },
    { label: t('cloud.menu.zip'), disabled: folder.files === 0, onSelect: () => actions.zipFolder(folder) },
    { kind: 'separator' },
    { label: t('cloud.menu.deleteFolder'), danger: true, onSelect: () => actions.deleteFolder(folder) },
  ];
}

/** O menu de uma mesa na lateral. */
export function roomMenu(
  ctx: Pick<MenuContext, 't'>,
  room: DeskRoom,
  actions: { openRoom: (room: DeskRoom) => void; uploadToRoom: (room: DeskRoom) => void; zipRoom: (room: DeskRoom) => void },
): MenuEntry[] {
  const { t } = ctx;
  return [
    { kind: 'title', label: room.name },
    { label: t('cloud.menu.openRoom'), onSelect: () => actions.openRoom(room) },
    ...(room.gm ? [{ label: t('cloud.menu.uploadToRoom'), onSelect: () => actions.uploadToRoom(room) }] : []),
    { label: t('cloud.menu.zipRoom'), disabled: room.files === 0, onSelect: () => actions.zipRoom(room) },
  ];
}

/** O menu da área vazia da lista. Na lixeira, "Esvaziar a lixeira". */
export function areaMenu(
  ctx: Pick<MenuContext, 't'>,
  state: { trash: boolean; canUpload: boolean; sort: FileSort; layout: 'grid' | 'list'; hasFiles: boolean },
  actions: { upload: () => void; newFolder: () => void; selectAll: () => void; setSort: (sort: FileSort) => void; setLayout: (layout: 'grid' | 'list') => void; emptyTrash: () => void },
): MenuEntry[] {
  const { t } = ctx;
  const view: MenuEntry[] = [
    { kind: 'submenu', label: t('cloud.menu.sortBy'), entries: (['recent', 'name', 'size'] as const).map((sort) => ({ label: t(`cloud.sort.${sort}`), checked: state.sort === sort, onSelect: () => actions.setSort(sort) })) },
    {
      kind: 'submenu',
      label: t('cloud.menu.viewAs'),
      entries: (['grid', 'list'] as const).map((layout) => ({ label: t(`cloud.layout.${layout}`), checked: state.layout === layout, onSelect: () => actions.setLayout(layout) })),
    },
  ];
  if (state.trash)
    return [
      { label: t('cloud.menu.selectAll'), shortcut: 'Ctrl+A', disabled: !state.hasFiles, onSelect: actions.selectAll },
      ...view,
      { kind: 'separator' },
      { label: t('cloud.menu.emptyTrash'), danger: true, disabled: !state.hasFiles, onSelect: actions.emptyTrash },
    ];
  return [
    ...(state.canUpload ? [{ label: t('cloud.upload'), onSelect: actions.upload }] : []),
    { label: t('cloud.newFolder'), onSelect: actions.newFolder },
    { label: t('cloud.menu.selectAll'), shortcut: 'Ctrl+A', disabled: !state.hasFiles, onSelect: actions.selectAll },
    { kind: 'separator' },
    ...view,
  ];
}

/** O menu de um envio na fila. */
export function uploadMenu(
  ctx: Pick<MenuContext, 't'>,
  upload: { status: 'waiting' | 'sending' | 'done' | 'error'; planLimited: boolean },
  actions: { cancel: () => void; retry: () => void; reveal: () => void; plans: () => void },
): MenuEntry[] {
  const { t } = ctx;
  if (upload.status === 'waiting' || upload.status === 'sending') return [{ label: t('cloud.uploads.cancel'), onSelect: actions.cancel }];
  if (upload.status === 'done') return [{ label: t('cloud.uploads.reveal'), onSelect: actions.reveal }];
  return [{ label: t('cloud.uploads.retry'), onSelect: actions.retry }, ...(upload.planLimited ? [{ label: t('cloud.uploads.plans'), onSelect: actions.plans }] : [])];
}

/**
 * O menu de um arquivo no painel da mesa: as ações do tipo nesta mesa, abrir, baixar, mostrar no Desk
 * Cloud e, para o dono, tirar desta mesa.
 */
export function panelMenu(
  ctx: Pick<MenuContext, 't'>,
  file: DeskFile,
  state: { gm: boolean; mine: boolean; roomId: string },
  actions: { use: (file: DeskFile, action: UseAction) => void; open: (file: DeskFile) => void; download: (file: DeskFile) => void; reveal: (file: DeskFile) => void; unlink: (file: DeskFile) => void },
): MenuEntry[] {
  const { t } = ctx;
  const use = actionsFor(file, state.gm).map((action): MenuEntry => ({ label: actionLabel(t, action), onSelect: () => actions.use(file, action) }));
  return [
    { kind: 'title', label: file.name },
    ...use,
    ...(use.length ? [{ kind: 'separator' } as const] : []),
    { label: t('cloud.menu.open'), onSelect: () => actions.open(file) },
    { label: t('cloud.menu.download'), onSelect: () => actions.download(file) },
    ...(state.mine ? [{ label: t('cloud.menu.reveal'), onSelect: () => actions.reveal(file) }] : []),
    ...(state.mine ? [{ kind: 'separator' } as const, { label: t('cloud.menu.unlink'), danger: true, onSelect: () => actions.unlink(file) }] : []),
  ];
}
